/**
 * `/c/:id/chat` — the actual chat experience.
 *
 * v2: thin wrapper around the cloned `EmbedRuntime` (see
 * `src/embed/`). The clone is intentionally a separate copy of
 * embed-client — fan platform features will diverge over time and
 * we don't want either side bound to the other's release cadence.
 *
 * Gating: this page assumes the user already owns the skin (acquired
 * via CharacterMarket at `/c/:id`). If they don't, we send them
 * back. Login redirect is handled inside the runtime's network
 * calls — the apiAuthHeaders registered in main.tsx threads the fan
 * id-token, and 401s from the backend trip the existing
 * fan-api `request()` retry. (For now the embed-runtime chat-client
 * just surfaces the error; full 401→signin on this page lands when
 * the embed-runtime grows a 401 hook.)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmbedRuntime } from '../embed/EmbedRuntime';
import { EnergyBattery } from '../components/EnergyBattery';
import { listMyOwnedCharacters, checkAccess, getCharacterIncident, type FanCharacterIncident } from '../api/fan-api';
import { getStoredFanClaims, fanCognitoConfigured } from '../auth/fan-auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type Gate =
  | { kind: 'loading' }
  | { kind: 'needs-login' }
  | { kind: 'not-owned' }
  | { kind: 'denied'; reason: string }
  | { kind: 'maintenance'; incident: FanCharacterIncident }
  | { kind: 'ready' };

export function CharacterChat() {
  const { characterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  const [gate, setGate] = useState<Gate>({ kind: 'loading' });

  useDocumentTitle('與角色對話');

  useEffect(() => {
    if (!characterId) return;
    const signedIn = Boolean(getStoredFanClaims()?.sub);
    if (!signedIn) {
      setGate({ kind: 'needs-login' });
      return;
    }
    let alive = true;
    (async () => {
      try {
        // Per `character-incident-monitor` — block chat entry when
        // this character has an open upstream-failure incident.
        // Checked before the access + ownership gates because the
        // operator probably hasn't realised their upstream is dead
        // yet; we don't want fans burning skin-acquire only to find
        // they can't actually talk.
        const incident = await getCharacterIncident(characterId).catch(() => null);
        if (!alive) return;
        if (incident) {
          setGate({ kind: 'maintenance', incident });
          return;
        }
        const access = await checkAccess(characterId);
        if (!alive) return;
        if (!access.allowed) {
          setGate({ kind: 'denied', reason: access.reason });
          return;
        }
        // Skin-ownership gate — chat requires acquisition (free or
        // paid). If not in the owned list, bounce to the market page.
        const owned = await listMyOwnedCharacters().catch(() => ({ items: [] }));
        if (!alive) return;
        const isOwned = owned.items.some((it) => it.characterId === characterId);
        setGate(isOwned ? { kind: 'ready' } : { kind: 'not-owned' });
      } catch (e) {
        if (!alive) return;
        setGate({ kind: 'denied', reason: (e as Error).message });
      }
    })();
    return () => { alive = false; };
  }, [characterId]);

  if (!characterId) return null;

  if (gate.kind === 'loading') {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6 sm:py-16 text-center">
        <div className="inline-block w-10 h-10 border-[3px] border-slate-700 border-t-indigo-400 rounded-full animate-spin" aria-label="載入中" />
      </div>
    );
  }

  // Shared gate-state shell — matches the outcome-page visual
  // language established in iter 9 (Confirm) and iter 12
  // (SignInCallback): centred big circular icon + h1 + body copy +
  // full-width primary CTA on mobile. Keeps the SPA's dead-end
  // surfaces visually coherent.
  if (gate.kind === 'needs-login') {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6 sm:py-16">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 space-y-5">
          <div className="flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-full bg-violet-500/15 border border-violet-500/40 flex items-center justify-center mb-3 text-violet-300 text-3xl">
              🔒
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold">需要登入</h1>
          </div>
          <p className="text-slate-400 text-sm text-center">想跟這個角色聊天請先登入或註冊。</p>
          {fanCognitoConfigured() && (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300"
            >登入 / 註冊</button>
          )}
        </div>
      </div>
    );
  }

  if (gate.kind === 'not-owned') {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6 sm:py-16">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 space-y-5">
          <div className="flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mb-3 text-amber-300 text-3xl">
              🔑
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold">尚未擁有此角色</h1>
          </div>
          <p className="text-slate-400 text-sm text-center">先到角色頁加入或解鎖，再回來開始聊天。</p>
          <button
            type="button"
            onClick={() => navigate(`/c/${characterId}`)}
            className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300"
          >前往角色頁</button>
        </div>
      </div>
    );
  }

  if (gate.kind === 'maintenance') {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6 sm:py-16">
        <div className="rounded-2xl border border-amber-500/30 bg-slate-900 p-5 sm:p-6 space-y-5">
          <div className="flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mb-3 text-amber-300 text-3xl">
              🛠
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold">維護中</h1>
          </div>
          <p className="text-slate-300 text-sm text-center leading-relaxed">
            這個角色目前無法對話 —— 我們的服務團隊已收到通知並處理中，請稍後再來看看。
          </p>
          <p className="text-slate-500 text-xs text-center">
            首次偵測：{new Date(gate.incident.firstSeenAt).toLocaleString()}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/c/${characterId}`)}
            className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300"
          >返回角色頁</button>
        </div>
      </div>
    );
  }

  if (gate.kind === 'denied') {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6 sm:py-16">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 space-y-5">
          <div className="flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mb-3 text-rose-300 text-3xl">
              ✕
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold">無法存取</h1>
          </div>
          <p className="text-slate-400 text-sm text-center break-words">{gate.reason}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-500"
          >回首頁</button>
        </div>
      </div>
    );
  }

  // The cloned EmbedRuntime fills the viewport edge-to-edge in
  // frame=none mode. main.tsx renders this route OUTSIDE Shell so
  // there's no AppHeader fighting for height — the runtime owns the
  // entire dvh. Chat history persists per-fan because the runtime's
  // chat-client thread the fan Cognito JWT (registered globally as
  // EmbedApiContext at boot in main.tsx), and the backend handler
  // writes turns under fanIdCharacterId.
  return (
    <div
      className="w-full relative"
      // --nx-batt-h = the top battery bar's height (incl. notch). The
      // EmbedRuntime stage + the back button read this var to sit BELOW
      // the bar so nothing overlaps the 3D head / character name.
      // 3.25rem(52px): 電池條實高約 38px + 14px 間隙 — 角色必須在
      // 電池「下方」保持一小段距離,不能貼著電池(電池鐵則)。
      style={{ height: '100dvh', ['--nx-batt-h' as string]: 'calc(3.25rem + env(safe-area-inset-top))' } as React.CSSProperties}>
      {/* Top-left back button overlay. Floats on top of the
          EmbedRuntime canvas. Goes back to /c/:id (the market page)
          rather than browser history so the user always lands on a
          predictable destination — deep-linked /chat entries from
          elsewhere still bounce back to the character page they
          belong to. */}
      {/* Back button overlay — top: env(safe-area-inset-top) so it
          clears the iOS notch when the SPA runs in standalone PWA
          mode. h-10 + px-3.5 keeps the chevron + 返回 affordance at
          a 40-44px touch target without dominating the canvas. */}
      <button
        type="button"
        onClick={() => navigate(`/c/${characterId}`)}
        aria-label="回到角色頁"
        className="absolute left-3 z-50 inline-flex items-center gap-1.5 h-10 rounded-full bg-slate-950/70 px-3.5 text-sm font-medium text-slate-100 backdrop-blur shadow-lg hover:bg-slate-900/80 focus:outline-none focus:ring-2 focus:ring-violet-500 active:scale-95 transition"
        style={{ top: 'calc(0.6rem + var(--nx-batt-h, 0px))' }}
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <span>返回</span>
      </button>
      <EmbedRuntime characterId={characterId} frameless />
      {/* Per `character-energy-system` — battery overlay (logged-in fan
          chat only; the anonymous 智能客服 embed never renders this page). */}
      {characterId && <EnergyBattery characterId={characterId} topBar />}
    </div>
  );
}
